import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CalendarOff, Paperclip, X } from 'lucide-react';
import { attendanceAPI, userAPI } from '../../services/api';
import { useToast } from '../UIProvider';
import useDialog from '../../hooks/useDialog';
import DatePicker from '../DatePicker';
import Select from '../Select';
import { clinicToday } from '../../utils/clinicTime';
import { formatDays } from './attendanceStatus';
import { FEATURES } from '../../config/features';

// Asking for leave. The form says what the range actually costs before it is sent: weekly offs and
// holidays inside it are not leave days, so three dates across a Sunday are two days. The server
// works that out (it knows the person's schedule and the holidays), so the preview is a call, not a
// guess here.
//
// When the balance is finished the server refuses with INSUFFICIENT_BALANCE and names the Unpaid
// type; the owner's rule (2026-10-02) is to offer Unpaid rather than turn the person away.
//
// `forUserId` is set when someone with Approve leave records leave on another person's behalf: it
// goes in already approved, and the balance is shown but never enforced.

const LeaveRequestDialog = ({ forUser = false, onClose, onSaved }) => {
  const notify = useToast();
  const [types, setTypes] = useState([]);
  const [users, setUsers] = useState([]);
  const [userId, setUserId] = useState('');
  const [typeId, setTypeId] = useState('');
  const [fromDate, setFromDate] = useState(clinicToday());
  const [toDate, setToDate] = useState(clinicToday());
  const [halfDay, setHalfDay] = useState(false);
  const [reason, setReason] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [offerUnpaid, setOfferUnpaid] = useState(null); // { unpaidTypeId, remaining, days }
  const fileRef = useRef(null);
  const dialogRef = useDialog(() => { if (!saving) onClose(); });

  useEffect(() => {
    let live = true;
    attendanceAPI.leaveTypes()
      .then((rows) => {
        if (!live) return;
        const list = Array.isArray(rows) ? rows : [];
        setTypes(list);
        setTypeId((current) => current || list[0]?.id || '');
      })
      .catch(() => { if (live) setError('The leave types could not be loaded.'); });
    return () => { live = false; };
  }, []);

  useEffect(() => {
    if (!forUser) return undefined;
    let live = true;
    userAPI.getAll({ status: 'ACTIVE' })
      .then((rows) => {
        if (!live) return;
        const list = Array.isArray(rows) ? rows : (rows?.content || []);
        setUsers(list.sort((a, b) => (a.fullName || '').localeCompare(b.fullName || '')));
      })
      .catch(() => { if (live) setUsers([]); });
    return () => { live = false; };
  }, [forUser]);

  // A half day is one date only, so picking it pulls the range together.
  useEffect(() => { if (halfDay && toDate !== fromDate) setToDate(fromDate); }, [halfDay, fromDate, toDate]);
  useEffect(() => { if (toDate < fromDate) setToDate(fromDate); }, [fromDate, toDate]);

  const ready = Boolean(typeId && fromDate && toDate && (!forUser || userId));
  const loadPreview = useCallback(async () => {
    if (!ready) { setPreview(null); return; }
    setPreviewing(true);
    try {
      const res = await attendanceAPI.leavePreview({ typeId, fromDate, toDate, halfDay, userId: forUser ? userId : undefined });
      setPreview(res);
      setOfferUnpaid(null);
    } catch {
      setPreview(null);
    } finally {
      setPreviewing(false);
    }
  }, [ready, typeId, fromDate, toDate, halfDay, forUser, userId]);
  useEffect(() => { loadPreview(); }, [loadPreview]);

  const type = useMemo(() => types.find((t) => t.id === typeId), [types, typeId]);

  const pickFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setError('The attachment must be 5 MB or smaller.'); return; }
    setError('');
    setAttachment(file);
  };

  const submit = async (overrideTypeId) => {
    if (!reason.trim()) { setError('Say why the leave is needed.'); return; }
    if (forUser && !userId) { setError('Pick the person.'); return; }
    setError('');
    setSaving(true);
    const payload = {
      typeId: overrideTypeId || typeId,
      fromDate,
      toDate,
      halfDay,
      reason: reason.trim(),
      ...(forUser ? { userId } : {}),
    };
    try {
      const saved = forUser
        ? await attendanceAPI.recordLeaveFor(payload, attachment)
        : await attendanceAPI.requestLeave(payload, attachment);
      notify('success', forUser ? 'Leave recorded' : 'Leave requested');
      onSaved(saved);
    } catch (e) {
      if (e?.code === 'INSUFFICIENT_BALANCE' && e?.data?.unpaidTypeId) {
        setOfferUnpaid({ unpaidTypeId: e.data.unpaidTypeId, remaining: e.data.remaining, days: e.data.days });
        setError('');
      } else {
        setError(e?.message || 'The request could not be sent.');
      }
      setSaving(false);
    }
  };

  // "3 days", or "3 days · not counted: 1 day off, Eid" when the range covers days that are not
  // leave. The server does the counting; this only says it out loud before the request is sent.
  const costLine = () => {
    if (previewing) return 'Working it out…';
    if (!preview) return '';
    const skipped = [];
    if (preview.offDays > 0) skipped.push(`${preview.offDays} day${preview.offDays === 1 ? '' : 's'} off`);
    (preview.holidays || []).forEach((h) => skipped.push(h.name));
    return skipped.length ? `${formatDays(preview.days)} · not counted: ${skipped.join(', ')}` : formatDays(preview.days);
  };

  const balanceLine = () => {
    // Allowances are parked while the clinic decides them, so the figure is not quoted here either.
    if (!FEATURES.leaveAllowances) return '';
    if (!preview || preview.quota == null) return type && type.paid === false ? 'Unpaid leave has no limit.' : '';
    const after = Number(preview.remaining) - Number(preview.days || 0);
    return `${type?.name} left after this: ${after < 0 ? 0 : after} of ${preview.quota}${preview.pending > 0 ? ` · ${formatDays(preview.pending)} already pending` : ''}`;
  };

  const title = forUser ? 'Record leave for someone' : 'Request leave';

  return (
    <div ref={dialogRef} className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" role="dialog" aria-modal="true" aria-label={title}>
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-w-md w-full max-h-[94vh] flex flex-col">
        <div className="bg-gradient-to-r from-navy-800 to-navy-700 p-5 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <CalendarOff className="w-4 h-4 text-white" />
            </div>
            <h2 className="text-lg font-bold text-white">{title}</h2>
          </div>
          <button type="button" aria-label="Close" onClick={onClose} disabled={saving} className="p-2 hover:bg-white/20 rounded-lg">
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          {forUser && (
            <Select
              label="Person *"
              value={userId}
              onChange={setUserId}
              options={users.map((u) => ({ value: u.id, label: u.fullName || u.username }))}
              placeholder="Pick the person"
              disabled={saving}
            />
          )}

          <Select
            label="Type *"
            value={typeId}
            onChange={(v) => { setTypeId(v); setOfferUnpaid(null); }}
            options={types.map((t) => ({
              value: t.id,
              label: `${t.name}${t.paid === false ? ' (unpaid)' : ''}`,
            }))}
            placeholder="Pick the type"
            disabled={saving}
          />

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold mb-1">From <span className="text-red-500">*</span></label>
              <DatePicker value={fromDate} onChange={setFromDate} disabled={saving} />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">To <span className="text-red-500">*</span></label>
              <DatePicker value={toDate} onChange={setToDate} min={fromDate} disabled={saving || halfDay} />
            </div>
          </div>

          <label className="flex items-center gap-2.5 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={halfDay}
              onChange={(e) => setHalfDay(e.target.checked)}
              disabled={saving}
              className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
            />
            <span>Half day only</span>
          </label>

          {(preview || previewing) && (
            <div className="rounded-xl bg-gray-50 border border-gray-200 px-3 py-2.5 text-sm">
              <p className="font-medium text-gray-900">{costLine()}</p>
              {balanceLine() && <p className="text-gray-600 text-xs mt-0.5">{balanceLine()}</p>}
            </div>
          )}

          <div>
            <label htmlFor="leave-reason" className="block text-sm font-semibold mb-1">Reason <span className="text-red-500">*</span></label>
            <textarea
              id="leave-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              disabled={saving}
              className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              placeholder="e.g. Fever, seen by Dr Sara"
            />
          </div>

          <div>
            <input ref={fileRef} type="file" accept="image/*,application/pdf" onChange={pickFile} className="hidden" />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={saving}
              className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
            >
              <Paperclip className="w-4 h-4" aria-hidden="true" /> {attachment ? 'Change attachment' : 'Attach a note'}
            </button>
            {attachment && (
              <p className="mt-1.5 text-xs text-gray-600 flex items-center gap-2">
                <span className="truncate">{attachment.name}</span>
                <button type="button" onClick={() => setAttachment(null)} className="text-red-600 hover:underline">Remove</button>
              </p>
            )}
          </div>

          {offerUnpaid && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 space-y-2.5">
              <p className="text-sm text-amber-900">
                {type?.name} leave has {formatDays(offerUnpaid.remaining)} left, and this is {formatDays(offerUnpaid.days)}.
                Send it as unpaid leave instead?
              </p>
              <div className="flex gap-2">
                <button type="button" onClick={() => setOfferUnpaid(null)} disabled={saving} className="flex-1 px-4 py-2 border border-gray-300 bg-white text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50">
                  Change the dates
                </button>
                <button type="button" onClick={() => submit(offerUnpaid.unpaidTypeId)} disabled={saving} className="flex-1 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium disabled:opacity-50">
                  Send as unpaid
                </button>
              </div>
            </div>
          )}

          {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
        </div>

        <div className="px-5 py-3 border-t bg-gray-50 flex justify-end gap-2 rounded-b-2xl">
          <button type="button" onClick={onClose} disabled={saving} className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm font-medium">Cancel</button>
          <button type="button" onClick={() => submit()} disabled={saving || !ready} className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium disabled:opacity-50">
            {saving ? 'Sending…' : forUser ? 'Record leave' : 'Send request'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LeaveRequestDialog;
